import { ArrowLeftOutlined } from '@ant-design/icons'
import { GraviButton, Horizontal, Texto, useThemeContext, Vertical } from '@gravitate-js/excalibrr'
import { Collapse, Spin } from 'antd'
import type { DriverSchedule } from '../../types'
import { useMemo } from 'react'
import { IconCircleCheckFilled } from '@tabler/icons-react'
import { groupStepsByOrder } from './util'
import { RouteStepItem } from './components/RouteStepItem'
import './styles.css'
import { DriverHeaderCard } from './components/DriverHeaderCard'
import type { MapboxMapHook } from '@/components/_SiteWide/MapboxMap/hooks/useMapboxMap'
import { useDriverTrackingState, actions } from '../../stores'
import { TractorIcon, TrailerIcon } from '@/components/_SiteWide/TruckIcons'

type RouteDetailViewProps = {
  driverScheduleNoBreadcrumbs: DriverSchedule | null | undefined
  driverScheduleWithBreadcrumbs: DriverSchedule
  isLoading: boolean
  handleClose: () => void
  flyToLocation: MapboxMapHook['flyToLocation']
  getMapLayerIds: MapboxMapHook['getMapLayerIds']
  toggleLayerVisibility: MapboxMapHook['toggleLayerVisibility']
}

export function RouteDetailView({
  driverScheduleNoBreadcrumbs,
  driverScheduleWithBreadcrumbs,
  isLoading,
  handleClose,
  flyToLocation,
  getMapLayerIds,
  toggleLayerVisibility,
}: RouteDetailViewProps) {
  const { isDarkTheme } = useThemeContext()
  const routeStepsByOrderNumber = useMemo(
    () => groupStepsByOrder(driverScheduleWithBreadcrumbs?.route),
    [driverScheduleWithBreadcrumbs?.route, isLoading]
  )
  const { driverOrdersOnRoute } = useDriverTrackingState()

  const currentStepIndex = useMemo(() => {
    return driverScheduleWithBreadcrumbs?.route?.findIndex((step) => step.current) || -1
  }, [driverScheduleWithBreadcrumbs?.route])

  const driverSchedule = driverScheduleWithBreadcrumbs || driverScheduleNoBreadcrumbs

  // Show spinner if no data at all is available (e.g. we are searching by driver name or order number)
  if (!driverSchedule) {
    return <Spinner />
  }

  return (
    <Vertical flex={1}>
      <GraviButton
        icon={<ArrowLeftOutlined />}
        buttonText='All Drivers'
        className='route-detail-back-button mb-2'
        size='small'
        onClick={handleClose}
        appearance='link'
      />
      <DriverHeaderCard driverSchedule={driverSchedule} showFlags={!isLoading} />
      <Horizontal justifyContent='space-between' className='vehicle-info-container p-2 mt-2' gap={12}>
        <Horizontal gap={4} verticalCenter>
          <TractorIcon />
          <Texto category='p1'>{driverSchedule.driver_log?.tractor || 'N/A'}</Texto>
        </Horizontal>
        <Horizontal gap={4} verticalCenter>
          <TrailerIcon />
          <Texto category='p1'>{driverSchedule.driver_log?.trailer || 'N/A'}</Texto>
        </Horizontal>
        <Texto>{`${driverSchedule.loaded_miles}/${driverSchedule.total_miles} mi` || 'N/A'}</Texto>
        <Texto>
          {driverSchedule.hours_assigned}/{driverSchedule.hours_available} hr
        </Texto>
      </Horizontal>
      {isLoading ? (
        <Spinner />
      ) : (
        <Vertical className='route-steps-container mt-3' gap={8}>
          {driverScheduleWithBreadcrumbs?.assigned_orders?.map((order) => {
            const isComplete =
              currentStepIndex >=
              routeStepsByOrderNumber[order.number]?.firstIndex + routeStepsByOrderNumber[order.number]?.steps?.length
            const isActive = driverOrdersOnRoute[order.number]?.isVisible
            const hasStarted = currentStepIndex >= routeStepsByOrderNumber[order.number]?.firstIndex
            return (
              <Collapse
                key={order.number}
                ghost
                activeKey={isActive ? [order.number] : []}
                style={{ border: '1px solid var(--gray-200)' }}
                expandIconPosition='right'
                items={[
                  {
                    key: order.number,
                    label: (
                      <Horizontal
                        onClick={() => {
                          const layerIds = getMapLayerIds().filter((id) => id.includes(`${order.number}`))
                          toggleLayerVisibility(layerIds)
                          actions.setOrderVisibility(order.number, !isActive)
                        }}
                        justifyContent='space-between'
                        alignItems='center'
                        className='p-2'
                      >
                        <Texto category='h4' weight='bold' className='flex items-center'>
                          Order {order.number}{' '}
                          {isComplete && (
                            <IconCircleCheckFilled size={22} className='ml-2' color='var(--theme-success)' />
                          )}
                        </Texto>
                        <div
                          className='route-color-indicator'
                          style={{
                            borderBottom: `6px solid ${hasStarted ? driverOrdersOnRoute[order.number]?.color : 'var(--gray-200)'}`,
                          }}
                        />
                      </Horizontal>
                    ),
                    children: (
                      <Vertical>
                        {routeStepsByOrderNumber[order.number]?.steps?.map((step, index) => (
                          <RouteStepItem
                            key={`${step.start_time}-${index}`}
                            step={step}
                            isLast={index === routeStepsByOrderNumber[order.number]?.steps?.length - 1}
                            timezone={driverScheduleWithBreadcrumbs.timezone}
                            onLocationClick={flyToLocation}
                          />
                        ))}
                      </Vertical>
                    ),
                  },
                ]}
              />
            )
          })}
        </Vertical>
      )}
    </Vertical>
  )
}

function Spinner() {
  return (
    <Vertical justifyContent='center' alignItems='center'>
      <Spin />
    </Vertical>
  )
}
